import React from 'react';
import { useQuery } from 'react-query';
import { getActivePets } from 'api/petApi';
import FetchError from 'types/http';
import { PetInterface } from 'types/pet';
import PageLoader from 'components/loaders/PageLoader';
import { PetAvatar, AvatarSizes, AvatarTypes } from '@chewy/pet-profile-pet-avatar';
import '@chewy/pet-profile-pet-avatar/dist/esm/main.css';
import './avatar-page.scss';

export default function AvatarPage() {
  const { data: activePets, status: activePetsStatus, error: activePetsError } = useQuery<
    PetInterface[],
    FetchError
  >(getActivePets.key, getActivePets, {
    refetchOnMount: true,
  });

  const loadingPets = activePetsStatus === 'loading' || activePetsStatus === 'idle';

  const error: FetchError | undefined = activePetsError || undefined;

  return (
    <>
      {loadingPets || error ? (
        <PageLoader />
      ) : (
        <div className="avatar-page">
          <h1>Pet avatar package</h1>
          <h2>Overview</h2>
          <p>
            The pet avatar package was created in order to help engineers at Chewy easily display
            pet avatars within their applications. <code>PetAvatar</code>'s only required prop is{' '}
            <code>src</code>, which accepts an <code>avatarUrl</code>. All other props are optional.
            All available props' functionality and look/feel is detailed in the below examples.
          </p>
          <h3>Importing</h3>
          <pre>
            <code>{`import { PetAvatar, AvatarSizes, AvatarTypes } from '@chewy/pet-profile-pet-avatar';
import '@chewy/pet-profile-pet-avatar/dist/esm/main.css';`}</code>
          </pre>
          <div className="example-grid">
            <div className="example-header">
              <h5>Default behavior</h5>
            </div>
            <div className="example-demo">
              {activePets && (
                <>
                  {activePets.map((pet, index) => (
                    <PetAvatar key={index} src={pet.avatarUrl} />
                  ))}
                </>
              )}
            </div>
            <div className="example-explanation">
              <pre>
                <code>
                  {`{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} />
))}`}
                </code>
              </pre>
            </div>
          </div>
          <h5>Sizing options</h5>
          <p>
            The <code>PetAvatar</code> component supports four sizes: small, medium, large, and
            huge. Small is the default size; it does not need to be passed in. It is purely listed
            here for demo and documentation purposes.
          </p>
          <div className="example-grid">
            <div className="example-demo avatar-group">
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar key={index} src={pet.avatarUrl} />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar key={index} src={pet.avatarUrl} size={AvatarSizes.MEDIUM} />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar key={index} src={pet.avatarUrl} size={AvatarSizes.LARGE} />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar key={index} src={pet.avatarUrl} size={AvatarSizes.HUGE} />
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className="example-explanation">
              <pre>
                <code>
                  {`{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} size={AvatarSizes.SMALL} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} size={AvatarSizes.MEDIUM} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} size={AvatarSizes.LARGE} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} size={AvatarSizes.HUGE} />
))}`}
                </code>
              </pre>
            </div>
          </div>
          <h5>Stroke options</h5>
          <p>
            The <code>PetAvatar</code> component supports four stroke options: default, MINT,
            Memorial - Deceased, and Memorial - Other. These can be mixed and matched with the above
            sizing options as desired.
          </p>
          <div className="example-grid">
            <div className="example-header">
              <h6>Default</h6>
              <p>
                The default stroke will show up without any additional props needed; it does not
                need to be passed in. It is purely listed here for demo and documentation purposes.
              </p>
            </div>
            <div className="example-demo avatar-group">
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.Default} />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.Default}
                        size={AvatarSizes.MEDIUM}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.Default}
                        size={AvatarSizes.LARGE}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.Default}
                        size={AvatarSizes.HUGE}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className="example-explanation">
              <pre>
                <code>
                  {`{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.Default} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.Default} size={AvatarSizes.MEDIUM} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.Default} size={AvatarSizes.LARGE} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.Default} size={AvatarSizes.HUGE} />
))}`}
                </code>
              </pre>
            </div>
          </div>
          <div className="example-grid">
            <div className="example-header">
              <h6>MINT</h6>
              <p>The MINT stroke type is generally only used within the MINT framework.</p>
            </div>
            <div className="example-demo avatar-group">
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MINT} />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MINT}
                        size={AvatarSizes.MEDIUM}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MINT}
                        size={AvatarSizes.LARGE}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MINT}
                        size={AvatarSizes.HUGE}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className="example-explanation">
              <pre>
                <code>
                  {`{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MINT} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MINT} size={AvatarSizes.MEDIUM} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MINT} size={AvatarSizes.LARGE} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MINT} size={AvatarSizes.HUGE} />
))}`}
                </code>
              </pre>
            </div>
          </div>
          <div className="example-grid">
            <div className="example-header">
              <h6>Memorial - Deceased</h6>
              <p>
                The Memorial - Deceased stroke type is generally used to display pets with a
                memorialized profile that has been marked as deceased.
              </p>
            </div>
            <div className="example-demo avatar-group">
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MemorialDeceased}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MemorialDeceased}
                        size={AvatarSizes.MEDIUM}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MemorialDeceased}
                        size={AvatarSizes.LARGE}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MemorialDeceased}
                        size={AvatarSizes.HUGE}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className="example-explanation">
              <pre>
                <code>
                  {`{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MemorialDeceased} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MemorialDeceased} size={AvatarSizes.MEDIUM} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MemorialDeceased} size={AvatarSizes.LARGE} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MemorialDeceased} size={AvatarSizes.HUGE} />
))}`}
                </code>
              </pre>
            </div>
          </div>
          <div className="example-grid">
            <div className="example-header">
              <h6>Memorial - Other</h6>
              <p>
                The Memorial - Other stroke type is generally used to display pets with a
                memorialized profile that are not specifically marked as deceased.
              </p>
            </div>
            <div className="example-demo avatar-group">
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MemorialOther}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MemorialOther}
                        size={AvatarSizes.MEDIUM}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MemorialOther}
                        size={AvatarSizes.LARGE}
                      />
                    ))}
                  </>
                )}
              </div>
              <div>
                {activePets && (
                  <>
                    {activePets.map((pet, index) => (
                      <PetAvatar
                        key={index}
                        src={pet.avatarUrl}
                        strokeType={AvatarTypes.MemorialOther}
                        size={AvatarSizes.HUGE}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className="example-explanation">
              <pre>
                <code>
                  {`{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MemorialOther} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MemorialOther} size={AvatarSizes.MEDIUM} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MemorialOther} size={AvatarSizes.LARGE} />
))}

{activePets.map((pet, index) => (
  <PetAvatar key={index} src={pet.avatarUrl} strokeType={AvatarTypes.MemorialOther} size={AvatarSizes.HUGE} />
))}`}
                </code>
              </pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
